import type { RenewalOption, RenewalTerms } from '../types'

/**
 * The one place proposal money is computed. The admin editor, the customer
 * proposal card, and the order form all call these, so what Ashley sees while
 * typing is exactly what the customer sees. Everything is rounded to cents
 * so 2,500 x $1.50 can never come out as $3,749.9999.
 */
export function cents(value: number): number {
  return Math.round(value * 100) / 100
}

export interface PricedLine {
  quantity: number | null
  unitPrice: number | null
}

/** quantity x unit price, or null when either side has not been entered. */
export function lineAmount(line: PricedLine): number | null {
  if (line.quantity === null || line.unitPrice === null) return null
  if (!Number.isFinite(line.quantity) || !Number.isFinite(line.unitPrice)) return null
  return cents(line.quantity * line.unitPrice)
}

export function sumLines(lines: PricedLine[]): number {
  return cents(lines.reduce((sum, line) => sum + (lineAmount(line) ?? 0), 0))
}

/** A discount never exceeds what it is discounting; a negative or missing amount is zero. */
export function appliedDiscount(subtotal: number, enabled: boolean, amount: number | null): number {
  if (!enabled || amount === null || !Number.isFinite(amount) || amount <= 0) return 0
  return cents(Math.min(subtotal, amount))
}

export interface OptionTotals {
  licenses: number
  addOns: number
  subtotal: number
  discount: number
  total: number
}

export function optionTotals(option: RenewalOption): OptionTotals {
  const licenses = lineAmount({ quantity: option.licenseQuantity, unitPrice: option.licenseUnitPrice }) ?? 0
  const addOns = sumLines(option.addOns)
  const subtotal = cents(licenses + addOns)
  const discount = appliedDiscount(subtotal, option.discountEnabled, option.discountAmount)
  return { licenses, addOns, subtotal, discount, total: cents(subtotal - discount) }
}

export interface ProposalWarning {
  key: string
  message: string
}

const PER_LICENSE_LABEL = /license/i

/**
 * Entry mistakes the math cannot catch on its own but a person would want
 * flagged before a customer sees the proposal. 2U's job board line was typed
 * as $2.68 instead of $3 and nothing said a word.
 */
export function optionWarnings(option: RenewalOption): ProposalWarning[] {
  const warnings: ProposalWarning[] = []
  const flagPrice = (key: string, name: string, price: number | null) => {
    if (price === null) return
    if (price <= 0) warnings.push({ key: `${key}-zero`, message: `${name} has no price. The line shows $0.` })
    else if (Math.round(price * 100) !== price * 100) warnings.push({ key: `${key}-fraction`, message: `${name} has a price of ${price}, which is a fraction of a cent.` })
    else if (price % 1 !== 0 && Math.round(price) !== price) warnings.push({ key: `${key}-cents`, message: `${name} is priced at $${price.toFixed(2)}. Confirm the cents are intended.` })
  }
  const licenseName = option.licenseLabel || 'Licenses'
  if (option.licenseQuantity !== null && option.licenseUnitPrice === null) warnings.push({ key: 'license-price', message: `${licenseName} has a quantity but no price.` })
  if (option.licenseQuantity === null && option.licenseUnitPrice !== null) warnings.push({ key: 'license-qty', message: `${licenseName} has a price but no quantity.` })
  flagPrice('license', licenseName, option.licenseUnitPrice)
  for (const addOn of option.addOns) {
    const name = addOn.name || 'An add on'
    if (addOn.quantity !== null && addOn.unitPrice === null) warnings.push({ key: `${addOn.id}-price`, message: `${name} has a quantity but no price.` })
    if (addOn.quantity === null && addOn.unitPrice !== null) warnings.push({ key: `${addOn.id}-qty`, message: `${name} has a price but no quantity.` })
    flagPrice(addOn.id, name, addOn.unitPrice)
    const perLicense = PER_LICENSE_LABEL.test(addOn.unitLabel || '')
    if (perLicense && addOn.quantity !== null && option.licenseQuantity !== null && addOn.quantity !== option.licenseQuantity) {
      warnings.push({ key: `${addOn.id}-mismatch`, message: `${name} is priced per license for ${addOn.quantity.toLocaleString('en-US')} but the option has ${option.licenseQuantity.toLocaleString('en-US')} licenses.` })
    }
  }
  if (option.discountEnabled) {
    const { subtotal } = optionTotals(option)
    if (option.discountAmount === null || option.discountAmount <= 0) warnings.push({ key: 'discount-empty', message: 'The discount is turned on but has no amount.' })
    else if (option.discountAmount > subtotal) warnings.push({ key: 'discount-exceeds', message: `The discount ($${option.discountAmount.toLocaleString('en-US')}) is larger than the subtotal, so the total shows $0.` })
  }
  if (option.termStart && option.termEnd && option.termEnd <= option.termStart) warnings.push({ key: 'term', message: 'The term end is not after the term start.' })
  if (option.termEnd && !/^20\d{2}-/.test(option.termEnd)) warnings.push({ key: 'term-year', message: `The term end year "${option.termEnd.slice(0, 4)}" looks wrong.` })
  if (option.termStart && !/^20\d{2}-/.test(option.termStart)) warnings.push({ key: 'term-start-year', message: `The term start year "${option.termStart.slice(0, 4)}" looks wrong.` })
  return warnings
}

/** The current-agreement strip is hand-entered; flag it when licenses x price disagrees with the stated total. */
export function currentTermsWarnings(terms: RenewalTerms): ProposalWarning[] {
  const warnings: ProposalWarning[] = []
  const computed = lineAmount({ quantity: terms.licenses, unitPrice: terms.perLicenseCost })
  if (computed !== null && terms.totalCost !== null && cents(terms.totalCost) !== computed) {
    warnings.push({ key: 'current-total', message: `Current agreement: ${terms.licenses!.toLocaleString('en-US')} x $${terms.perLicenseCost} is $${computed.toLocaleString('en-US')}, but the total entered is $${terms.totalCost.toLocaleString('en-US')}. Fine if the total includes add-ons; otherwise fix one of them.` })
  }
  if (terms.startDate && terms.endDate && terms.endDate <= terms.startDate) warnings.push({ key: 'current-term', message: 'Current agreement: the end date is not after the start date.' })
  return warnings
}
